import React, { useEffect, useState, useRef } from 'react';
import './FeatureStyles.css';
import './VisualModelFeature.css';

const VisualModelAnimation = () => {
  const [nodeOffsets, setNodeOffsets] = useState({});
  const [phase, setPhase] = useState('idle'); // idle | moving | clicked | flyout
  const [cursorPos, setCursorPos] = useState({ x: 80, y: 220 });
  const animationRef = useRef(null);
  const phaseRef = useRef('idle');
  const cursorStartTime = useRef(0);
  const cursorFrom = useRef({ x: 80, y: 220 });

  // State cards matching real app: dark cards with title, description, type badge
  const nodes = [
    { id: 'login', x: 20, y: 40, label: 'Login', desc: 'Authentication form with email...', type: 'FORM', typeColor: '#ec4899' },
    { id: 'dashboard', x: 180, y: 115, label: 'Dashboard', desc: 'Main overview with widgets...', type: 'LIST', typeColor: '#3b82f6' },
    { id: 'users', x: 320, y: 20, label: 'Users: List', desc: 'Paginated table of users with...', type: 'LIST', typeColor: '#3b82f6' },
    { id: 'detail', x: 370, y: 175, label: 'User: Detail', desc: 'Detailed user profile with edit...', type: 'DETAIL', typeColor: '#f59e0b' },
    { id: 'settings', x: 50, y: 200, label: 'Settings', desc: 'Application config form with...', type: 'FORM', typeColor: '#ec4899' },
  ];

  const edges = [
    { from: 'login', to: 'dashboard', label: 'Login success' },
    { from: 'dashboard', to: 'users', label: 'Navigate to users' },
    { from: 'users', to: 'detail', label: 'Click row' },
    { from: 'dashboard', to: 'settings', label: '' },
    { from: 'detail', to: 'dashboard', label: '' },
  ];

  const cardW = 130;
  const cardH = 78;

  const flyoutItems = [
    { label: 'Show information', color: '#3b82f6' },
    { label: 'Show tests', color: '#ec4899' },
    { label: 'Suggest happy tests', color: '#22c55e' },
    { label: 'Suggest sad tests', color: '#f59e0b' },
    { label: 'Suggest edge tests', color: '#a855f7' },
    { label: 'Suggest uncovered', color: '#ec4899' },
  ];

  // Cursor + flyout interaction cycle
  useEffect(() => {
    const startCycle = () => {
      cursorFrom.current = { x: 80, y: 220 };
      cursorStartTime.current = Date.now();
      phaseRef.current = 'moving';
      setPhase('moving');
    };

    startCycle();
    const interval = setInterval(startCycle, 8000);
    return () => clearInterval(interval);
  }, []);

  // Phase transitions after cursor arrives
  useEffect(() => {
    if (phase === 'moving') {
      const t1 = setTimeout(() => { phaseRef.current = 'clicked'; setPhase('clicked'); }, 1400);
      return () => clearTimeout(t1);
    }
    if (phase === 'clicked') {
      const t2 = setTimeout(() => { phaseRef.current = 'flyout'; setPhase('flyout'); }, 350);
      return () => clearTimeout(t2);
    }
    if (phase === 'flyout') {
      const t3 = setTimeout(() => { phaseRef.current = 'idle'; setPhase('idle'); }, 4000);
      return () => clearTimeout(t3);
    }
  }, [phase]);

  useEffect(() => {
    const usersBase = nodes.find(n => n.id === 'users');
    const targetX = usersBase.x + cardW / 2;
    const targetY = usersBase.y + cardH / 2;

    const animate = () => {
      const time = Date.now() / 1000;
      const newOffsets = {};
      nodes.forEach((node, idx) => {
        const speed = 0.25 + idx * 0.05;
        const amplitude = 10 + idx * 2;
        newOffsets[node.id] = {
          x: Math.sin(time * speed + idx * 0.9) * amplitude,
          y: Math.cos(time * speed * 0.5 + idx * 1.3) * amplitude * 0.8
        };
      });
      setNodeOffsets(newOffsets);

      // Animate cursor position during 'moving' phase
      const p = phaseRef.current;
      if (p === 'moving') {
        const elapsed = (Date.now() - cursorStartTime.current) / 1400; // 0..1 over 1.4s
        const t = Math.min(elapsed, 1);
        // Ease out cubic
        const ease = 1 - Math.pow(1 - t, 3);
        const from = cursorFrom.current;
        setCursorPos({
          x: from.x + (targetX - from.x) * ease,
          y: from.y + (targetY - from.y) * ease
        });
      } else if (p === 'clicked' || p === 'flyout') {
        // Keep cursor at target (with slight offset from card floating)
        const usersOffset = newOffsets['users'] || { x: 0, y: 0 };
        setCursorPos({
          x: targetX + usersOffset.x,
          y: targetY + usersOffset.y
        });
      }

      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  const getAnimatedPos = (node) => {
    const offset = nodeOffsets[node.id] || { x: 0, y: 0 };
    return { x: node.x + offset.x, y: node.y + offset.y };
  };

  const getNodeById = (id) => nodes.find(n => n.id === id);

  const getCurvedPath = (fromNode, toNode) => {
    const from = getAnimatedPos(fromNode);
    const to = getAnimatedPos(toNode);
    const fromCx = from.x + cardW / 2;
    const fromCy = from.y + cardH / 2;
    const toCx = to.x + cardW / 2;
    const toCy = to.y + cardH / 2;

    const midX = (fromCx + toCx) / 2;
    const midY = (fromCy + toCy) / 2;
    const dx = toCx - fromCx;
    const dy = toCy - fromCy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const curveAmount = len * 0.3;
    const cx = midX - (dy / len) * curveAmount;
    const cy = midY + (dx / len) * curveAmount;

    return { path: `M ${fromCx} ${fromCy} Q ${cx} ${cy} ${toCx} ${toCy}`, midX: cx, midY: cy };
  };

  return (
    <div className="feature-animation visual-model-clean">
      <svg className="graph-layer-full" viewBox="-10 -10 530 310" preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id="vm-arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#64748b" />
          </marker>
          <filter id="card-shadow" x="-10%" y="-10%" width="120%" height="130%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
          </filter>
        </defs>

        {/* Edges */}
        {edges.map((edge) => {
          const fromNode = getNodeById(edge.from);
          const toNode = getNodeById(edge.to);
          if (!fromNode || !toNode) return null;
          const { path, midX, midY } = getCurvedPath(fromNode, toNode);

          return (
            <g key={`${edge.from}-${edge.to}`}>
              <path
                d={path}
                className="vm-edge-line"
                markerEnd="url(#vm-arrow)"
              />
              {edge.label && (
                <text x={midX} y={midY - 5} className="vm-edge-label" textAnchor="middle">
                  {edge.label}
                </text>
              )}
            </g>
          );
        })}

        {/* State cards */}
        {nodes.map((node) => {
          const pos = getAnimatedPos(node);
          return (
            <g key={node.id} className="vm-state-card">
              {/* Card background */}
              <rect
                x={pos.x}
                y={pos.y}
                width={cardW}
                height={cardH}
                rx="6"
                className="vm-card-bg"
                filter="url(#card-shadow)"
              />
              {/* Title */}
              <text x={pos.x + 14} y={pos.y + 22} className="vm-card-title">
                {node.label}
              </text>
              {/* Description */}
              <text x={pos.x + 14} y={pos.y + 37} className="vm-card-desc">
                {node.desc.length > 28 ? node.desc.slice(0, 28) + '...' : node.desc}
              </text>
              {/* Type badge - outlined style */}
              <rect
                x={pos.x + 12}
                y={pos.y + cardH - 23}
                width={node.type.length * 6 + 10}
                height={13}
                rx="3"
                fill="none"
                stroke={node.typeColor}
                strokeWidth="0.8"
              />
              <text
                x={pos.x + 12 + (node.type.length * 6 + 10) / 2}
                y={pos.y + cardH - 13.5}
                className="vm-card-badge-text"
                textAnchor="middle"
                fill={node.typeColor}
              >
                {node.type}
              </text>
            </g>
          );
        })}

        {/* Click ripple */}
        {phase === 'clicked' && (
          <circle cx={cursorPos.x} cy={cursorPos.y} r="4" className="vm-click-ripple" />
        )}

        {/* Flyout menu from Users card */}
        {phase === 'flyout' && (() => {
          const usersNode = getNodeById('users');
          const pos = getAnimatedPos(usersNode);
          const flyX = pos.x + cardW - 16;
          const flyY = pos.y + 4;
          const flyW = 130;
          const itemH = 21;
          const headerH = 36;
          const flyH = headerH + flyoutItems.length * itemH + 14;
          return (
            <g className="vm-flyout">
              {/* Flyout background */}
              <rect x={flyX} y={flyY} width={flyW} height={flyH} rx="8" className="vm-flyout-bg" />
              {/* Header */}
              <text x={flyX + 14} y={flyY + 15} className="vm-flyout-header-label">STATE</text>
              <text x={flyX + 14} y={flyY + 28} className="vm-flyout-header-title">Users: List</text>
              {/* Separator */}
              <line x1={flyX + 12} y1={flyY + headerH} x2={flyX + flyW - 12} y2={flyY + headerH} stroke="#334155" strokeWidth="0.5" />
              {/* Items */}
              {flyoutItems.map((item, i) => {
                const iy = flyY + headerH + 10 + i * itemH;
                return (
                  <g key={i} className="vm-flyout-item" style={{ animationDelay: `${0.08 + i * 0.06}s` }}>
                    <circle cx={flyX + 20} cy={iy + 5} r="3.5" fill={item.color} opacity="0.9" />
                    <text x={flyX + 30} y={iy + 8.5} className="vm-flyout-item-text">{item.label}</text>
                  </g>
                );
              })}
            </g>
          );
        })()}

        {/* Animated cursor */}
        {(phase === 'moving' || phase === 'clicked' || phase === 'flyout') && (
          <g className={`vm-cursor ${phase === 'clicked' ? 'clicking' : ''}`} transform={`translate(${cursorPos.x}, ${cursorPos.y})`}>
            <path
              d="M0,0 L0,14 L4,11 L7,17 L9,16 L6,10 L11,10 Z"
              fill="white"
              stroke="#1e293b"
              strokeWidth="1"
              strokeLinejoin="round"
            />
          </g>
        )}
      </svg>
    </div>
  );
};

const VisualModelFeature = {
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="3" />
      <circle cx="4" cy="6" r="2" />
      <circle cx="20" cy="6" r="2" />
      <circle cx="4" cy="18" r="2" />
      <circle cx="20" cy="18" r="2" />
      <path d="M6 6.5L9.5 10M14.5 10L18 6.5M6 17.5L9.5 14M14.5 14L18 17.5" />
    </svg>
  ),
  title: "Visual Application Model",
  description: "Real visual application model insights rendered by AI. Your application model is always there - tracking change drift, visualizing trends, and powering detailed reports. Understand user flows, identify coverage gaps, and see exactly how your application evolves over time.",
  animation: <VisualModelAnimation />
};

export default VisualModelFeature;
